import { useCallback, useEffect, useState } from "react";

/** Child → host: the reader's full content height. */
export const HEIGHT_MESSAGE = "tezlens:height";
/** Host → child: where the host's viewport currently sits over the frame. */
export const VIEWPORT_MESSAGE = "tezlens:viewport";
/** Low-frequency safety net for when observers are throttled. */
const POLL_INTERVAL_MS = 750;
/** Height changes smaller than this are ignored as sub-pixel churn. */
const HEIGHT_EPSILON = 8;

export interface EmbedBridge {
  embedded: boolean;
  /**
   * True once the host has told us where its viewport is. Until then, anything
   * that has to be positioned against the visible region must not render — an
   * older host page that has not been given the updated snippet never sends
   * this, and we would otherwise place things at arbitrary offsets.
   */
  hostAware: boolean;
  /** Document-space y of the top of the region the reader is visible in. */
  visibleTop: number;
  /**
   * Pixels of the host's viewport covered by its own fixed furniture (a sticky
   * site header, an admin bar). Anything the reader pins has to clear this or
   * it ends up behind the host's header.
   */
  topInset: number;
  scrollToTop: () => void;
}

function inIframe(): boolean {
  try {
    return window.self !== window.top;
  } catch {
    // Cross-origin access to window.top throws, which itself means we are framed.
    return true;
  }
}

/**
 * Bridge between the reader and whatever is hosting it.
 *
 * Standalone, the reader scrolls in its own viewport and the numbers here come
 * straight from `window`. Embedded, the host sizes the iframe to the reader's
 * full height, which means the iframe never scrolls and its "viewport" is the
 * entire document — `position: fixed` pins to the bottom of a 10,000px page and
 * `position: sticky` never activates. So the host posts its own scroll position
 * and viewport height, and the reader positions against that instead.
 *
 * Browsers also throttle rendering work in iframes that are offscreen or not
 * being composited: requestAnimationFrame and ResizeObserver can both go
 * completely silent there. Timers keep running, so the height is republished on
 * a cheap interval and the observers are kept only as a fast path.
 */
export function useEmbedBridge(): EmbedBridge {
  const [embedded] = useState(inIframe);
  const [hostAware, setHostAware] = useState(false);
  const [visibleTop, setVisibleTop] = useState(0);
  const [topInset, setTopInset] = useState(0);

  // --- child → host: content height -------------------------------------
  useEffect(() => {
    if (!embedded) return undefined;

    let lastSent = 0;
    let stopped = false;

    const publish = () => {
      if (stopped) return;
      const height = Math.ceil(
        Math.max(
          document.documentElement.scrollHeight,
          document.body ? document.body.scrollHeight : 0
        )
      );
      if (height <= 0 || Math.abs(height - lastSent) < HEIGHT_EPSILON) return;
      lastSent = height;
      window.parent.postMessage({ type: HEIGHT_MESSAGE, height }, "*");
    };

    let observer: ResizeObserver | undefined;
    if (typeof ResizeObserver !== "undefined") {
      observer = new ResizeObserver(publish);
      observer.observe(document.documentElement);
      if (document.body) observer.observe(document.body);
    }
    const mutations = new MutationObserver(publish);
    mutations.observe(document.body, { childList: true, subtree: true });

    window.addEventListener("load", publish);
    window.addEventListener("resize", publish);
    const poll = setInterval(publish, POLL_INTERVAL_MS);
    publish();

    return () => {
      stopped = true;
      clearInterval(poll);
      observer?.disconnect();
      mutations.disconnect();
      window.removeEventListener("load", publish);
      window.removeEventListener("resize", publish);
    };
  }, [embedded]);

  // --- host → child: visible region -------------------------------------
  useEffect(() => {
    if (!embedded) return undefined;

    const onMessage = (event: MessageEvent) => {
      const data = event.data;
      if (!data || data.type !== VIEWPORT_MESSAGE) return;

      // The host's origin is not known ahead of time, so rather than trusting
      // the sender, a single number is read and it is clamped. (The host also
      // sends its viewport height; nothing needs it yet.)
      const frameTop = Number(data.frameTop);
      if (!Number.isFinite(frameTop)) return;

      const inset = Number(data.topInset);
      setTopInset(
        Number.isFinite(inset) ? Math.min(Math.max(inset, 0), 400) : 0
      );

      const docHeight = document.documentElement.scrollHeight;
      const top = Math.min(Math.max(-frameTop, 0), Math.max(docHeight - 1, 0));

      setHostAware(true);
      setVisibleTop((previous) =>
        Math.abs(previous - top) < 2 ? previous : top
      );
    };

    window.addEventListener("message", onMessage);
    return () => window.removeEventListener("message", onMessage);
  }, [embedded]);

  // --- standalone: read our own scroll ----------------------------------
  useEffect(() => {
    if (embedded) return undefined;

    // Read synchronously rather than deferring to requestAnimationFrame:
    // scrollY and innerHeight are already-computed values so this forces no
    // layout, and rAF does not run in a throttled or backgrounded frame, which
    // would leave the toolbar and back-to-top button reading a stale scroll
    // position. The state setter below bails out when nothing moved.
    const read = () => setVisibleTop(window.scrollY);

    read();
    window.addEventListener("scroll", read, { passive: true });
    window.addEventListener("resize", read);
    return () => {
      window.removeEventListener("scroll", read);
      window.removeEventListener("resize", read);
    };
  }, [embedded]);

  /**
   * Only meaningful standalone. Embedded, the iframe is sized to the reader's
   * full height and does not scroll — the host page owns scrolling, and
   * supplies its own back-to-top control.
   */
  const scrollToTop = useCallback(() => {
    window.scrollTo({ top: 0, behavior: "smooth" });
  }, []);

  return { embedded, hostAware, visibleTop, topInset, scrollToTop };
}
